/**
 * TicketDetailPage — thread view for one ticket. Polls every 15s for fresh
 * replies (cheap on the user side because tickets are scoped to the caller).
 */
import React, { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'motion/react'
import { Icons } from '@/src/modules/components/Icons'
import { cn } from '@/src/infrastructure/lib/utils'
import {
  getMyTicket,
  replyToTicket,
  getNotifications,
  markNotificationRead,
  type ApiSupportTicket,
  type SupportTicketMessage,
} from '@/src/infrastructure/lib/api'
import {
  TICKET_PRIORITY_TONE,
  TICKET_STATUS_LABEL,
  TICKET_STATUS_TONE,
} from './constants'

function formatTime(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function MessageBubble({ msg }: { msg: SupportTicketMessage; key?: React.Key }) {
  const isUser = msg.senderType === 'USER'
  const isSystem = msg.senderType === 'SYSTEM'
  return (
    <motion.li
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn('flex gap-2', isUser ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap',
          isUser
            ? 'bg-primary text-white rounded-br-sm'
            : isSystem
              ? 'bg-amber-500/10 text-amber-700 border border-amber-500/30 italic'
              : 'bg-surface-low border border-outline-variant/40 text-on-surface rounded-bl-sm',
        )}
      >
        {!isUser && !isSystem && (
          <p className="text-[10px] font-semibold mb-1 text-on-surface-variant">
            {msg.sender?.name ?? 'Support'} · {formatTime(msg.createdAt)}
          </p>
        )}
        {msg.message}
        {msg.attachments.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {msg.attachments.map((a) => (
              <a
                key={a.id}
                href={a.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px]',
                  isUser
                    ? 'border-white/40 text-white'
                    : 'border-outline-variant/40 text-on-surface-variant hover:text-primary',
                )}
              >
                <Icons.ExternalLink size={10} />
                Attachment
              </a>
            ))}
          </div>
        )}
        {isUser && (
          <p className="text-[10px] text-white/70 mt-1 text-right">
            {formatTime(msg.createdAt)}
          </p>
        )}
      </div>
    </motion.li>
  )
}

export function TicketDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [ticket, setTicket] = useState<ApiSupportTicket | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [reply, setReply] = useState('')
  const [sending, setSending] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    const load = () => {
      getMyTicket(id)
        .then((t) => !cancelled && setTicket(t))
        .catch((err) => !cancelled && setError(err.message))
        .finally(() => !cancelled && setLoading(false))
    }
    load()
    const interval = setInterval(load, 15_000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [id])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [ticket?.messages.length])

  // After the ticket loads and the user has had a moment to see the messages,
  // mark any unread notifications tied to this ticket as read so the bell badge
  // clears without requiring individual clicks.
  useEffect(() => {
    if (!id || !ticket) return
    const timer = window.setTimeout(async () => {
      try {
        const page = await getNotifications({ unreadOnly: true, limit: 50 })
        const ticketNotifs = page.items.filter(
          (n) => (n.metadata as Record<string, unknown>)['ticketId'] === id,
        )
        await Promise.all(
          ticketNotifs.map((n) =>
            markNotificationRead(n.id).catch((err) => {
              console.error('[TicketDetail] failed to mark notification read', n.id, err)
            }),
          ),
        )
      } catch (err) {
        console.error('[TicketDetail] failed to fetch notifications for auto-read', err)
      }
    }, 700)
    return () => window.clearTimeout(timer)
  }, [id, ticket?.id])

  async function send(e: React.FormEvent) {
    e.preventDefault()
    if (!id || !reply.trim() || sending) return
    setSending(true)
    setError('')
    try {
      const updated = await replyToTicket(id, { message: reply.trim() })
      setTicket(updated)
      setReply('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reply')
    } finally {
      setSending(false)
    }
  }

  if (loading && !ticket) {
    return (
      <div className="p-10 text-center text-on-surface-variant">Loading ticket…</div>
    )
  }

  if (!ticket) {
    return (
      <div className="p-10 text-center">
        <p className="text-sm text-on-surface-variant">{error || 'Ticket not found.'}</p>
        <Link
          to="/dashboard/help/tickets"
          className="mt-4 inline-flex text-xs font-semibold text-primary hover:underline"
        >
          ← Back to tickets
        </Link>
      </div>
    )
  }

  const isClosed = ticket.status === 'CLOSED'

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 max-w-3xl mx-auto space-y-5">
      <Link
        to="/dashboard/help/tickets"
        className="inline-flex items-center gap-1 text-xs font-semibold text-on-surface-variant hover:text-on-surface"
      >
        <Icons.ChevronLeft size={14} /> All tickets
      </Link>

      {/* Header */}
      <header className="rounded-3xl border border-outline-variant/40 bg-surface-low/40 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant">
              {ticket.category} · #{ticket.id.slice(0, 8)}
            </p>
            <h1 className="mt-1 text-xl md:text-2xl font-bold text-on-surface">
              {ticket.subject}
            </h1>
            <p className="mt-1 text-[11px] text-on-surface-variant">
              Created {formatTime(ticket.createdAt)}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={cn(
                'rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                TICKET_STATUS_TONE[ticket.status],
              )}
            >
              {TICKET_STATUS_LABEL[ticket.status]}
            </span>
            <span
              className={cn(
                'text-[10px] font-semibold uppercase',
                TICKET_PRIORITY_TONE[ticket.priority],
              )}
            >
              {ticket.priority}
            </span>
          </div>
        </div>
      </header>

      {/* Thread */}
      <section className="rounded-3xl border border-outline-variant/40 bg-surface-low/40 p-4 md:p-6 space-y-3">
        <ul className="space-y-3">
          <AnimatePresence initial={false}>
            {ticket.messages.map((m) => (
              <MessageBubble key={m.id} msg={m} />
            ))}
          </AnimatePresence>
        </ul>
        <div ref={bottomRef} />
      </section>

      {/* Reply box */}
      {isClosed ? (
        <div className="rounded-2xl border border-dashed border-outline-variant/50 p-4 text-center text-sm text-on-surface-variant">
          This ticket is closed.{' '}
          <Link
            to="/dashboard/help/tickets/new"
            className="font-semibold text-primary hover:underline"
          >
            Open a new ticket
          </Link>{' '}
          if you have a follow-up.
        </div>
      ) : (
        <form
          onSubmit={send}
          className="rounded-2xl border border-outline-variant/40 bg-surface-low/40 p-4 space-y-3"
        >
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply to support…"
            rows={4}
            className="w-full rounded-xl border border-outline-variant/40 bg-surface px-3 py-2 text-sm focus:border-primary focus:outline-none resize-y"
          />
          {error && <p className="text-xs text-rose-700">{error}</p>}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!reply.trim() || sending}
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs font-semibold shadow-sm transition-colors',
                reply.trim() && !sending
                  ? 'bg-primary text-white hover:bg-brand-deep'
                  : 'bg-surface-low text-on-surface-variant cursor-not-allowed',
              )}
            >
              {sending ? 'Sending…' : 'Send reply'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
